import { CoreCommon } from '@b2c/core';
import { ApplicationContextService, FalconContext } from '@b2c/services/auth';
import { AnalyticsHelper, CurrencyInfoQueryParams } from '@b2c/services/common';
import { useState } from 'react';
import { useQueryParams } from './use-query-params';

const CurrencyStorageKey = 'app_currency';
const LocaleStorageKey = 'app_locale';

/**
 * 将非英语市场的币种映射到对应语言。
 * 仅映射明确的市场币种；USD/EUR/GBP 等不在表内，
 * 命中不到时由调用方回退到浏览器语言，最后默认语言。
 */
export const CurrencyLanguageMap: Record<string, CoreCommon.LanguageCode> = {
  JPY: 'ja-JP',
  CNY: 'zh-CN',
  TWD: 'zh-TW',
  HKD: 'zh-TW',
};

/**
 * 归一化浏览器语言码到系统支持的语言（en-US / ja-JP / zh-CN / zh-TW）。
 * navigator.language 的中文变体较多（zh-Hans-*、zh-Hant-*、zh-HK 等），
 * 统一归并为简体 zh-CN 或繁体 zh-TW。
 */
export function normalizeLanguageCode(rawCode: string): CoreCommon.LanguageCode {
  // 简体中文：zh / zh-Hans / zh-Hans-* / zh-CN / zh-CHS
  if (
    rawCode === 'zh' ||
    rawCode === 'zh-CN' ||
    rawCode === 'zh-CHS' ||
    rawCode.startsWith('zh-Hans')
  ) {
    return 'zh-CN';
  }
  // 繁体中文：zh-TW / zh-HK / zh-MO / zh-Hant 及变体 → 统一 zh-TW（与 HKD 映射一致）
  if (
    rawCode.startsWith('zh-TW') ||
    rawCode.startsWith('zh-HK') ||
    rawCode.startsWith('zh-MO') ||
    rawCode.startsWith('zh-Hant') ||
    rawCode === 'zh-CHT'
  ) {
    return 'zh-TW';
  }
  return (
    {
      en: 'en-US',
      ja: 'ja-JP',
    }[rawCode] || (rawCode as any)
  );
}

/** 根据币种查语言，大小写不敏感；未命中返回 undefined。 */
export function getLanguageFromCurrency(
  currency?: string
): CoreCommon.LanguageCode | undefined {
  if (!currency) {
    return undefined;
  }
  return CurrencyLanguageMap[currency.toUpperCase()];
}

function getBrowserContextLocale() {
  const storageLocale = localStorage.getItem(
    LocaleStorageKey
  ) as CoreCommon.LanguageCode;
  if (storageLocale) {
    return storageLocale;
  }
  return normalizeLanguageCode(navigator.language);
}

/**
 * 计算首次访问时传给后端的语言，优先级：
 * 1. 已加载的服务端上下文语言（登录态/上下文信息）
 * 2. 币种到语言的映射（仅当 URL 指定了明确币种且可映射）
 * 3. 浏览器上下文语言（localStorage app_locale -> navigator.language）
 * 4. 默认语言
 */
export function resolveInitialLanguage(
  appContextLocale: CoreCommon.LanguageCode | undefined,
  currencyFromUrl: string | undefined,
  browserLocale: CoreCommon.LanguageCode | undefined,
  defaultLanguage: CoreCommon.LanguageCode
): CoreCommon.LanguageCode {
  return (
    appContextLocale ||
    getLanguageFromCurrency(currencyFromUrl) ||
    browserLocale ||
    defaultLanguage
  );
}

export const useAppContext = function (
  defaultLanguageType: CoreCommon.LanguageCode = 'en-US',
  defaultCurrency = 'USD',
  fixedSetting = false
) {
  const [appContext, setAppContext] = useState<FalconContext.AppContextInfo>();
  const { queryParams } = useQueryParams<CurrencyInfoQueryParams>();

  function loadAppContext(
    currencyCode?: string,
    languageCode?: CoreCommon.LanguageCode
  ) {

    if (fixedSetting) {
      currencyCode = defaultCurrency;
      languageCode = defaultLanguageType;
    } else {
      if (!currencyCode) {
        currencyCode =
          appContext?.currencyInfo.Currency ||
          queryParams.cur ||
          localStorage.getItem(CurrencyStorageKey) ||
          defaultCurrency;
      }
      if (!languageCode) {
        languageCode = resolveInitialLanguage(
          appContext?.locale,
          queryParams.cur,
          getBrowserContextLocale(),
          defaultLanguageType
        );
      }
    }
    
    return ApplicationContextService.getContext({
      Currency: currencyCode,
      LanguageCode: languageCode,
    }).then((resp) => {
      localStorage.setItem(CurrencyStorageKey, resp.currencyInfo.Currency);
      localStorage.setItem(LocaleStorageKey, resp.locale);
      setAppContext(resp);
      if (resp.userID) {
        AnalyticsHelper.setUserId(resp.userID);
      }
      return resp;
    });
  }

  return {
    appContext,
    setAppContext,
    loadAppContext,
  };
};
